import React from 'react';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Award, Star, Crown, Gem } from 'lucide-react';
import type { EmployeeGrade } from '@/types/timesheet';
import { cn } from '@/lib/utils';

interface EmployeeGradeBadgeProps {
  grade?: EmployeeGrade;
  gradeLevel?: number;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
}

/**
 * Returns badge styling config based on the numeric grade level (1-5).
 * Falls back to grey if level is not provided or out of range.
 */
const getConfigByLevel = (level: number | undefined, label: string) => {
  switch (level) {
    case 1:
      return {
        icon: TrendingUp,
        label,
        className: 'bg-success-100 text-success-700 border-success-200 dark:bg-success-900/30 dark:text-success-300 dark:border-success-800',
      };
    case 2:
      return {
        icon: Award,
        label,
        className: 'bg-info-100 text-info-700 border-info-200 dark:bg-info-900/30 dark:text-info-300 dark:border-info-800',
      };
    case 3:
      return {
        icon: Star,
        label,
        className: 'bg-blueviolet-100 text-blueviolet-700 border-blueviolet-200 dark:bg-blueviolet-900/30 dark:text-blueviolet-300 dark:border-blueviolet-800',
      };
    case 4:
      return {
        icon: Crown,
        label,
        className: 'bg-warning-100 text-warning-700 border-warning-200 dark:bg-warning-900/30 dark:text-warning-300 dark:border-warning-800',
      };
    case 5:
      return {
        icon: Gem,
        label,
        className: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800',
      };
    default:
      return {
        icon: Award,
        label,
        className: 'bg-neutral-100 text-neutral-700 border-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-700',
      };
  }
};

export const EmployeeGradeBadge: React.FC<EmployeeGradeBadgeProps> = ({
  grade,
  gradeLevel,
  size = 'md',
  showIcon = true,
  className,
}) => {
  if (!grade) return null;

  const config = getConfigByLevel(gradeLevel, grade);
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'text-xs h-5',
    md: 'text-xs h-6',
    lg: 'text-sm h-7',
  };

  const iconSizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-3.5 w-3.5',
    lg: 'h-4 w-4',
  };

  return (
    <Badge
      variant="outline"
      className={cn(
        'font-medium',
        sizeClasses[size],
        config.className,
        className
      )}
    >
      {showIcon && <Icon className={cn(iconSizeClasses[size], 'mr-1')} />}
      {config.label}
    </Badge>
  );
};
